import { useCallback, useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface ActionResult {
    success: boolean;
    error?: string;
}

export interface UseAutoSaveOptions {
    save: () => Promise<ActionResult>;
    data: Record<string, any>;
    enabled: boolean;
    debounceMs?: number;
    onError?: (error: string) => void;
    /**
     * Optional ref the consumer can flip to `true` to indicate that the next
     * observed `data` change should be treated as a baseline (e.g. seed data
     * from a panel mounting), not as a save-worthy edit. The hook clears the
     * flag after consuming it.
     */
    skipAsBaselineRef?: React.MutableRefObject<boolean>;
}

export interface UseAutoSaveReturn {
    autoSaveStatus: AutoSaveStatus;
    lastSavedAt: Date | null;
    cancelPendingAutoSave: () => void;
}

const DEFAULT_DEBOUNCE_MS = 2000;

const serializeData = (value: unknown): string => {
    try {
        return JSON.stringify(value ?? null);
    } catch {
        return '';
    }
};

export function useAutoSave({
    save,
    data,
    enabled,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    onError,
    skipAsBaselineRef,
}: UseAutoSaveOptions): UseAutoSaveReturn {
    const [status, setStatus] = useState<AutoSaveStatus>('idle');
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSavingRef = useRef(false);
    const retryAfterSaveRef = useRef(false);
    const saveRef = useRef(save);
    const onErrorRef = useRef(onError);
    const enabledRef = useRef(enabled);
    const dataRef = useRef(data);
    const lastPersistedSnapshotRef = useRef<string>(serializeData(data));
    const lastObservedSnapshotRef = useRef<string>(serializeData(data));

    saveRef.current = save;
    onErrorRef.current = onError;
    enabledRef.current = enabled;
    dataRef.current = data;

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const performSave = useCallback(async () => {
        if (isSavingRef.current) {
            retryAfterSaveRef.current = true;
            return;
        }

        if (!enabledRef.current) return;

        const snapshotAtSaveStart = serializeData(dataRef.current);
        isSavingRef.current = true;
        setStatus('saving');

        try {
            const result = await saveRef.current();
            if (result.success) {
                lastPersistedSnapshotRef.current = snapshotAtSaveStart;
                lastObservedSnapshotRef.current = snapshotAtSaveStart;
                setLastSavedAt(new Date());
                setStatus('saved');
            } else {
                setStatus('error');
                onErrorRef.current?.(result.error ?? 'Auto-save failed');
            }
        } catch (err: any) {
            setStatus('error');
            onErrorRef.current?.(err?.message ?? 'Auto-save failed');
        } finally {
            isSavingRef.current = false;

            if (retryAfterSaveRef.current) {
                retryAfterSaveRef.current = false;
                performSave();
            }
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            clearTimer();
            return;
        }

        const currentSnapshot = serializeData(data);

        if (skipAsBaselineRef?.current) {
            // Caller flagged this data change as seed/baseline (e.g. a panel
            // just mounted and emitted its initial values). Adopt it as the
            // new baseline without arming a save.
            skipAsBaselineRef.current = false;
            lastPersistedSnapshotRef.current = currentSnapshot;
            lastObservedSnapshotRef.current = currentSnapshot;
            clearTimer();
            return;
        }

        if (currentSnapshot === lastPersistedSnapshotRef.current) {
            // Content has not actually changed since the last persist —
            // don't arm the debounce or flicker the indicator.
            clearTimer();
            lastObservedSnapshotRef.current = currentSnapshot;
            return;
        }

        if (currentSnapshot === lastObservedSnapshotRef.current) {
            // Re-render with no real content change since the previous effect run.
            // Leave any pending debounce alone so an unrelated re-render does not
            // extend the wait window.
            return;
        }

        lastObservedSnapshotRef.current = currentSnapshot;

        clearTimer();
        setStatus('pending');
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            performSave();
        }, debounceMs);
    }, [data, enabled, debounceMs, clearTimer, performSave, skipAsBaselineRef]);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);

    const cancelPendingAutoSave = useCallback(() => {
        clearTimer();
        if (status === 'pending') {
            setStatus('idle');
        }
    }, [clearTimer, status]);

    return {
        autoSaveStatus: status,
        lastSavedAt,
        cancelPendingAutoSave,
    };
}
